import React from "react";
import {useTranslation} from "react-i18next";
import styled from "styled-components";
import {ws} from "styles/tokens";
import {CtaArrowIcon, GradientCtaButton} from "./onboardingStyles";
import PricingSection from "./PricingSection";

const HARDWARE_CHIPS = [
  {key: "appleWatch", name: "Apple Watch", src: "/assets/images/onboarding/chip-applewatch.png"},
  {key: "garmin", name: "Garmin", src: "/assets/images/onboarding/chip-garmin.png"},
  {key: "vakaros", name: "Vakaros", src: "/assets/images/onboarding/chip-vakaros.png"},
  {key: "coros", name: "Coros", src: "/assets/images/onboarding/chip-coros.png"},
  {key: "suunto", name: "Suunto", src: "/assets/images/onboarding/chip-suunto.png"},
  {key: "wearOs", name: "Wear OS", src: "/assets/images/onboarding/chip-wearos.png"}
];

const SPORT_CARDS = [
  {key: "sailing", image: "/assets/images/onboarding/sport-sailing.jpg"},
  {key: "wingfoil", image: "/assets/images/onboarding/sport-wingfoil.jpg"},
  {key: "windsurf", image: "/assets/images/onboarding/sport-windsurf.jpg"},
  {key: "sup", image: "/assets/images/onboarding/sport-sup.jpg"}
];

const HeroHeader = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  padding: 48px 120px;

  @media (max-width: 980px) {
    padding: 40px 24px;
  }
`;

const HeroTitle = styled.h1`
  margin: 0;
  max-width: 822px;
  font-family: ${ws.fontDisplay};
  font-size: 48px;
  font-weight: 300;
  line-height: 56px;
  letter-spacing: -0.03em;
  color: ${ws.fg};
  text-align: center;

  @media (max-width: 980px) {
    font-size: 34px;
    line-height: 42px;
  }
`;

const SocialProofBadge = styled.div`
  padding: 8px 18px;
  border-radius: ${ws.radiusPill};
  background: ${ws.tint};
  font-family: ${ws.fontBody};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${ws.accentText};
`;

const HeroSubtitle = styled.p`
  margin: 0;
  max-width: 720px;
  font-family: ${ws.fontBody};
  font-size: 18px;
  line-height: 28px;
  color: ${ws.muted};
  text-align: center;
`;

const SportCardsGrid = styled.section`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  width: 100%;
  padding: 24px 120px;

  @media (max-width: 980px) {
    padding: 24px;
  }
`;

const SportCard = styled.div`
  display: flex;
  flex: 1 0 0;
  min-width: 200px;
  flex-direction: column;
  gap: 16px;
`;

const SportImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: ${ws.radiusXl};
`;

const SportMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SportTitle = styled.h3`
  margin: 0;
  font-family: ${ws.fontDisplay};
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: ${ws.fg};
`;

const SportText = styled.p`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 14px;
  color: ${ws.muted};
`;

const CtaSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
  padding: 36px 24px 71px;
`;

const CompatibilityGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 17px;
  padding-top: 24px;
`;

const CompatibilityTitle = styled.h2`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ws.accentText};
  text-align: center;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 16px;
  max-width: 623px;
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 170px;
  height: 40px;
  border: 1px solid ${ws.lineStrong};
  border-radius: ${ws.radiusPill};
  /* Stays white in both themes: the hardware logos are dark brand artwork */
  background: #fff;
`;

const ChipLogo = styled.img`
  max-width: 100px;
  max-height: 24px;
  object-fit: contain;
`;

const ButtonsArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const FreeToStart = styled.p`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 16px;
  color: ${ws.muted};
`;

interface Props {
  onGetStarted: () => void;
}

function LandingStep({onGetStarted}: Props) {
  const {t} = useTranslation();

  return (
    <>
      <HeroHeader>
        <HeroTitle>{t("onboarding.landing.heroTitle")}</HeroTitle>
        <SocialProofBadge>{t("onboarding.landing.socialProof")}</SocialProofBadge>
        <HeroSubtitle>{t("onboarding.landing.heroSubtitle")}</HeroSubtitle>
      </HeroHeader>
      <SportCardsGrid>
        {SPORT_CARDS.map((card, i) => (
          <SportCard key={card.key}>
            {/* The first card is in view on load; the others wait until the user scrolls to them. */}
            <SportImage
              src={card.image}
              alt={t(`onboarding.landing.${card.key}Title`)}
              width={1200}
              height={800}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
            />
            <SportMeta>
              <SportTitle>{t(`onboarding.landing.${card.key}Title`)}</SportTitle>
              <SportText>{t(`onboarding.landing.${card.key}Text`)}</SportText>
            </SportMeta>
          </SportCard>
        ))}
      </SportCardsGrid>
      <PricingSection />
      <CtaSection>
        <CompatibilityGroup>
          <CompatibilityTitle>{t("onboarding.landing.compatibleWith")}</CompatibilityTitle>
          <ChipsRow>
            {HARDWARE_CHIPS.map((chip) => (
              <Chip key={chip.key}>
                <ChipLogo src={chip.src} alt={chip.name} />
              </Chip>
            ))}
          </ChipsRow>
        </CompatibilityGroup>
        <ButtonsArea>
          <GradientCtaButton onClick={onGetStarted}>
            {t("onboarding.landing.createFreeAccount")}
            <CtaArrowIcon src="/assets/images/onboarding/arrow-cta.svg" alt="" />
          </GradientCtaButton>
          <FreeToStart>{t("onboarding.landing.freeToStart")}</FreeToStart>
        </ButtonsArea>
      </CtaSection>
    </>
  );
}

export default LandingStep;
